import { updateAgent } from '@/services/api';
import { defaultErrorHandler } from '@/utils/default-error-handler';
import { IAgent, IAgentData } from '@d-id/types';
import { AxiosError } from 'axios';
import { MutationFunction, useMutation } from 'react-query';
import { AGENT_DEFAULT_NAME } from '../constants';
import { useCreateKnowledgeAndDocuments, useUpdateKnowledgeAndDocuments } from './knowledge';

const RETRIES = 3;

export const useUpdateAgent = (customErrorHandler?: (error: any) => Promise<void>) => {
    const { mutateAsync: updateKnowledge } = useUpdateKnowledgeAndDocuments();
    const { mutateAsync: createKnowledge } = useCreateKnowledgeAndDocuments();

    const editAgentMut: MutationFunction<IAgent | undefined, any> = async ({
        update,
        agentId,
        knowledgeId,
        uploadingFiles,
        deletedFiles,
    }) => {
        let knowledge = undefined;
        if (knowledgeId) {
            await updateKnowledge({
                knowledgeId,
                uploadingFiles,
                deletedFiles,
            });
        } else if (!!update.llm?.template && update.llm?.template !== 'assistant') {
            knowledge = await createKnowledge({ agentName: update?.preview_name ?? AGENT_DEFAULT_NAME, uploadingFiles });
            knowledgeId = knowledge.id;
        }

        const agentUpdates: { agentId: string; update: Partial<IAgentData> } = {
            agentId,
            update: {
                ...update,
                ...(!!knowledge && {
                    knowledge: {
                        ...update.knowledge,
                        id: knowledgeId,
                    },
                }),
            },
        };

        return updateAgent(agentUpdates);
    };

    return useMutation('edit-agent', editAgentMut, {
        retry(failureCount, error: AxiosError) {
            return (
                failureCount < RETRIES &&
                !!error.response?.status &&
                (error.response.status === 403 || error.response.status === 401 || error.response.status >= 500)
            );
        },
        onError: customErrorHandler || defaultErrorHandler,
    });
};
